import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import GetCookie from "../../../../utils/GetCookie";
import authNavigate from "../../../../globalComponents/AuthNav";

import ProcessingPopup from "../../../../utils/popup/ProcessingPopup";
import ErrorPopup from "../../../../utils/popup/ErrorPopup";
import SuccessPopup from "../../../../utils/popup/SuccessPopup";

import DashboardHeader from "../DashboardHeader";
import ClinicalTrialsResultsList from "./ClinicalTrialsResultsList";
import ClinicalTrialsDetailPanel from "./ClinicalTrialsDetailPanel";
import DownloadDropdown from "../DownloadDropdown";
import MigrateDropdown from "../MigrateDropdown";
import { StatusBadgeXSm } from "../../../../utils/StatusBadge";
import LiteratureDiscoveryGraphComponent from "../LiteratureDiscoveryGraphComponent";

// ── Progress steps for Clinical Trials search ──
const SEARCH_STEPS = [
    { upTo: 19,  label: "Searching ClinicalTrials.gov",  icon: "bi-search",         desc: "Querying ClinicalTrials.gov for matching records" },
    { upTo: 59,  label: "Fetching trial metadata",       icon: "bi-cloud-download",  desc: "Downloading titles, conditions & interventions" },
    { upTo: 79,  label: "Building geographical map",     icon: "bi-geo-alt",         desc: "Mapping trial locations by country" },
    { upTo: 89,  label: "Generating timeline graph",     icon: "bi-bar-chart-line",  desc: "Charting trials over time" },
    { upTo: 100, label: "Saving results",                icon: "bi-database-check",  desc: "Storing trials and analytics to database" },
];

const CT_PHASES = [
    { id: "ABSTRACT",  label: "Study Data (CSV)",             shortLabel: "Study Data" },
    { id: "FULL_TEXT", label: "Study Documents (PDFs)", shortLabel: "Documents"  },
];

const DiscoveryProgressBar = ({ progress }) => {
    const pct        = Math.min(Math.max(progress || 0, 0), 100);
    const activeIdx  = SEARCH_STEPS.findIndex(s => pct <= s.upTo);
    const currentIdx = activeIdx === -1 ? SEARCH_STEPS.length - 1 : activeIdx;

    return (
        <div className="vh-55 d-flex flex-column justify-content-center p-4">
            <div className="d-flex align-items-center justify-content-between mb-3">
                <div className="d-flex align-items-center gap-2">
                    <div className="discovery-step-icon-circle">
                        <i className={`bi ${SEARCH_STEPS[currentIdx].icon} ev-fs-14`} style={{ color: "#fff" }} />
                    </div>
                    <div>
                        <p className="ev-reg-p-dark ev-bold-med mb-0">{SEARCH_STEPS[currentIdx].label}…</p>
                        <p className="ev-small-p-dark text-muted mb-0">Clinical Trials search in progress</p>
                    </div>
                </div>
                <span className="ev-bold-med discovery-pct-label">{pct}%</span>
            </div>

            <div className="discovery-progress-track">
                <div
                    className="discovery-progress-fill"
                    style={{ width: `${pct}%`, animation: pct < 100 ? "gradient-move 3s linear infinite" : "none" }}
                />
            </div>

            <div className="row g-2">
                {SEARCH_STEPS.map((s, i) => {
                    const done   = pct >= s.upTo;
                    const active = i === currentIdx;
                    const state  = done ? "done" : active ? "active" : "pending";
                    return (
                        <div key={i} className="col-4 col-md-2">
                            <div
                                className="ev-card-2 discovery-step-card h-100 d-flex flex-column align-items-start"
                                style={{
                                    border:   active ? "1px solid var(--color9)" : "1px solid transparent",
                                    opacity:  done || active ? 1 : 0.45,
                                    transition: "opacity 0.4s, border-color 0.4s",
                                }}
                            >
                                <div className={`discovery-step-icon-box ${state}`}>
                                    {done
                                        ? <i className="bi bi-check-lg ev-fs-13" style={{ color: "#fff" }} />
                                        : <i className={`bi ${s.icon} ev-fs-13`} style={{ color: active ? "var(--color1)" : "var(--color17)" }} />
                                    }
                                </div>
                                <p className="ev-small-p-dark ev-bold-med mb-0">{s.label}</p>
                                <p className="ev-small-p-dark mb-0 mt-1">{s.desc}</p>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};


const ClinicalTrialsDiscoveryDashboard = ({ search, onBack }) => {
    const navigate = useNavigate();

    const API_BASE_URL = "/pceapi/slr/discovery";
    const headers = useMemo(() => ({ Authorization: GetCookie("access_token") }), []);

    const [results, setResults]               = useState([]);
    const [selectedResult, setSelectedResult] = useState(null);
    const [totalCount, setTotalCount]         = useState(0);

    const [queryPrompt, setQueryPrompt] = useState("");
    const [finalQuery, setFinalQuery]   = useState(search.query || "");
    const [limit, setLimit]             = useState(null);
    const [fromDate, setFromDate]       = useState("");
    const [toDate, setToDate]           = useState("");

    const [activeTab, setActiveTab]                     = useState("results");
    const [statusGraphData, setStatusGraphData]         = useState(null);
    const [geoGraphData, setGeoGraphData]               = useState(null);
    const [interventionsGraphData, setInterventionsGraphData] = useState(null);
    const [timelineGraphData, setTimelineGraphData]     = useState(null);
    const [graphsLoading, setGraphsLoading]             = useState(false);
    const [showDiscoveryAccess, setShowDiscoveryAccess] = useState(false);

    const [selectedIds, setSelectedIds]         = useState(new Set());
    const [selectedUserIds, setSelectedUserIds] = useState([]);

    const [selectedProject, setSelectedProject] = useState(null);
    const [selectedPhase, setSelectedPhase]     = useState(CT_PHASES[0]);
    const [slrProjects, setSlrProjects] = useState([]);

    const [searchProcessingStatus, setSearchProcessingStatus]       = useState(null);
    const [abstractMigrationStatus, setAbstractMigrationStatus]     = useState(null);
    const [fulltextMigrationStatus, setFulltextMigrationStatus]     = useState(null);
    const [progressBar, setProgressBar]                             = useState(0);

    const [isProcessing, setIsProcessing]   = useState(false);
    const [processingMessage, setProcessingMessage] = useState("");
    const [isGenerating, setIsGenerating]   = useState(false);
    const [isRefreshing, setIsRefreshing]   = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [isMigrating, setIsMigrating]     = useState(false);
    const [downloadType, setDownloadType]   = useState("abstract");

    const [errorMessage, setErrorMessage]     = useState("");
    const [successMessage, setSuccessMessage] = useState("");

    const isInitialSelection = useRef(true);
    const isSearchRunning    = searchProcessingStatus === "RUNNING";

    const selectAll = useMemo(
        () => results.length > 0 && results.every(r => selectedIds.has(r.id)),
        [results, selectedIds]
    );

    const activeSelectedIds = useMemo(
        () => [...selectedIds].filter(id => results.some(r => r.id === id)),
        [selectedIds, results]
    );

    const extractYear = (dateStr) => {
        if (!dateStr) return null;
        return new Date(dateStr).getFullYear().toString();
    };

    // ── Fetch Discovery Details ──
    const fetchDiscoveryDetails = async (from = null, to = null) => {
        if (!isSearchRunning) {
            setIsProcessing(true);
            setProcessingMessage("Loading search results...");
        }
        try {
            const res = await axios.post(
                `${API_BASE_URL}/view-details`,
                { discovery_id: search.id, sort_from: from, sort_to: to },
                { headers }
            );

            if (res.data?.status === "NotAuthorized") { authNavigate('/login', 'iam', navigate); return; }

            if (res.data?.status === "success") {
                const payload = res.data.data;

                setSearchProcessingStatus(payload.processing_status);
                setAbstractMigrationStatus(payload.abstract_migration_status || null);
                setFulltextMigrationStatus(payload.fulltext_migration_status || null);
                setProgressBar(payload.estimated_completion_at || 0);
                setTotalCount(payload.total_results || 0);
                setFinalQuery(payload.search_query || "");

                const studies = payload.studies || [];
                setResults(studies);

                if (studies.length) {
                    if (isInitialSelection.current) {
                        setSelectedIds(new Set(studies.map(r => r.id)));
                        isInitialSelection.current = false;
                    } else {
                        const validIds = new Set(studies.map(r => r.id));
                        setSelectedIds(prev => new Set([...prev].filter(id => validIds.has(id))));
                    }
                    if (!selectedResult) setSelectedResult(studies[0]);
                } else {
                    setSelectedResult(null);
                    setSelectedIds(new Set());
                }

                setSelectedUserIds(payload.discovery_access?.map(u => u.user_id) || []);
            } else {
                setErrorMessage(res.data?.message || "Failed to load discovery details");
            }
        } catch (err) {
            setErrorMessage(err.message || "Error fetching discovery details");
        } finally {
            setIsProcessing(false);
        }
    };

    const fetchGraphs = async () => {
        if (!search?.id) return;
        setGraphsLoading(true);
        try {
            const res = await axios.post(
                `${API_BASE_URL}/ct-graphs`,
                { discovery_id: search.id },
                { headers }
            );
            if (res.data?.status === "success") {
                const data = res.data.data;
                setStatusGraphData(data.status_chart           ? { figure_dict: data.status_chart }           : null);
                setGeoGraphData(data.geo_chart                 ? { figure_dict: data.geo_chart }               : null);
                setInterventionsGraphData(data.interventions_chart ? { figure_dict: data.interventions_chart } : null);
                setTimelineGraphData(data.timeline_chart       ? { figure_dict: data.timeline_chart }          : null);
            }
        } catch (err) {
            console.error("Error fetching CT graphs:", err);
        } finally {
            setGraphsLoading(false);
        }
    };

    // ── Fetch SLR Projects ──
    const fetchSLRProjects = async () => {
        try {
            const res = await axios.post(
                `/pceapi/slr/list`,
                { page: 1, size: 100 },
                { headers }
            );
            if (res.data?.status === "success") setSlrProjects(res.data.data || []);
        } catch (err) {
            console.error("Failed to fetch SLR projects", err);
        }
    };

    // ── Save Access ──
    const saveAccess = async () => {
        setIsProcessing(true);
        setProcessingMessage("Saving access...");
        try {
            const res = await axios.post(
                `${API_BASE_URL}/save-user-access`,
                { discovery_id: search.id, user_ids: selectedUserIds },
                { headers }
            );
            if (res.data?.status === "NotAuthorized") { authNavigate('/login', 'iam', navigate); return; }
            if (res.data?.status === "success") {
                setSuccessMessage("Access saved successfully");
                setShowDiscoveryAccess(false);
            } else {
                setErrorMessage(res.data?.message || "Failed to save access");
            }
        } catch (err) {
            setErrorMessage(err.message || "Error saving access");
        } finally {
            setIsProcessing(false);
        }
    };

    const handleUserSelection = (userId) => {
        setSelectedUserIds(prev => prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]);
    };

    const handleSelectResult = (result) => setSelectedResult(result);

    // ── Run Search ──
    const handleRunSearch = async () => {
        setIsProcessing(true);
        setProcessingMessage("Running Clinical Trials search...");
        try {
            const res = await axios.post(
                `${API_BASE_URL}/run`,
                { discovery_id: search.id, search_query: finalQuery, search_limit: limit || null },
                { headers }
            );
            if (res.data?.status === "NotAuthorized") { authNavigate('/login', 'iam', navigate); return; }
            if (res.data?.status === "success") {
                setSuccessMessage(res.data.message || "Clinical Trials search started");
                setSearchProcessingStatus("RUNNING");
                await fetchDiscoveryDetails();
            } else {
                setErrorMessage(res.data?.message || "Failed to run search");
            }
        } catch (err) {
            setErrorMessage(err.response?.data?.message || "Error running search");
        } finally {
            setIsProcessing(false);
        }
    };

    // ── Refresh ──
    const handleRefresh = async () => {
        setIsRefreshing(true);
        setErrorMessage("");
        try {
            await fetchDiscoveryDetails(extractYear(fromDate), extractYear(toDate));
        } finally {
            setIsRefreshing(false);
        }
    };

    // ── Generate Query ──
    const handleGenerateQuery = async () => {
        if (!queryPrompt.trim() || isProcessing || isGenerating) return;
        setIsGenerating(true);
        setProcessingMessage("Generating query...");
        setErrorMessage("");
        try {
            const res = await axios.post(
                `${API_BASE_URL}/generate-ct-query`,
                { natural_language_query: queryPrompt.trim() },
                { headers }
            );
            if (res.data?.status === "NotAuthorized") { authNavigate('/login', 'iam', navigate); return; }
            if (res.data?.status === "success") {
                setFinalQuery(res.data.data?.ct_query || "");
                setSuccessMessage("Query generated successfully");
            } else {
                setErrorMessage(res.data?.message || "Failed to generate query");
            }
        } catch (err) {
            setErrorMessage(err.message || "Error generating query");
        } finally {
            setIsGenerating(false);
            setProcessingMessage("");
        }
    };

    // ── Download ──
    const handleDownload = async () => {
        if (activeSelectedIds.length === 0) return;
        setIsDownloading(true);
        setProcessingMessage("Downloading...");
        try {
            const selectedStudyIds = results
                .filter(r => activeSelectedIds.includes(r.id))
                .map(r => r.id);

            // downloadType "abstract" → "csv", "full_text" → "documents"
            const ctType = downloadType === "full_text" ? "documents" : "csv";
            const filename = ctType === "csv"
                ? `clinical_trials_${search.id}.csv`
                : `clinical_trials_documents_${search.id}.zip`;

            const res = await axios.post(
                `${API_BASE_URL}/download-ct`,
                {
                    discovery_id:       search.id,
                    selected_study_ids: selectedStudyIds,
                    type:               ctType,
                },
                { headers, responseType: "blob" }
            );

            const url  = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement("a");
            link.href  = url;
            link.setAttribute("download", filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err) {
            setErrorMessage("Download failed");
        } finally {
            setIsDownloading(false);
            setProcessingMessage("");
        }
    };

    // ── Migrate ──
    const handleMigrate = async ({ project, phase } = {}) => {
        const activeProject = project || selectedProject;
        if (!activeProject || activeSelectedIds.length === 0) return;

        const isDocumentMigration = (phase?.id || phase) === "FULL_TEXT";
        const endpoint = isDocumentMigration
            ? `${API_BASE_URL}/migrate-ct-documents`
            : `${API_BASE_URL}/migrate-ct-study`;

        setIsMigrating(true);
        setProcessingMessage(isDocumentMigration ? "Migrating documents..." : "Migrating studies...");
        try {
            const selectedStudyIds = results
                .filter(r => activeSelectedIds.includes(r.id))
                .map(r => r.id);

            const res = await axios.post(
                endpoint,
                {
                    discovery_id:       search.id,
                    selected_study_ids: selectedStudyIds,
                    fabric_id:          activeProject.fabric_id ?? null,
                    thread_id:          activeProject.thread_id ?? null,
                },
                { headers }
            );

            if (res.data?.status === "NotAuthorized") { authNavigate('/login', 'iam', navigate); return; }

            if (res.data?.status === "success") {
                setSuccessMessage("Migration started successfully");
                if (isDocumentMigration) {
                    setFulltextMigrationStatus("RUNNING");
                } else {
                    setAbstractMigrationStatus("RUNNING");
                }
                await fetchDiscoveryDetails();
            } else {
                setErrorMessage(res.data?.message || "Migration failed");
            }
        } catch (err) {
            setErrorMessage("Migration failed");
        } finally {
            setIsMigrating(false);
            setProcessingMessage("");
        }
    };

    // ── Initial load ──
    useEffect(() => {
        fetchDiscoveryDetails();
        fetchSLRProjects();
        fetchGraphs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="mx-1 pb-4">
            <DashboardHeader
                searchProcessingStatus={searchProcessingStatus}
                isProcessing={isProcessing}
                showDiscoveryAccess={showDiscoveryAccess}
                setShowDiscoveryAccess={setShowDiscoveryAccess}
                selectedUserIds={selectedUserIds}
                onUserSelection={handleUserSelection}
                onSaveAccess={saveAccess}
                ownerEmail={search.owner}
                apiBaseUrl={API_BASE_URL}
                onRunSearch={handleRunSearch}
                onRefresh={handleRefresh}
                onBack={onBack}
            />

            {/* ── Query + Controls ── */}      
            <div className="mb-2">
                <div className="d-flex justify-content-between align-items-end mb-2">
                    <label className="ev-small-p-dark ev-bold-reg mb-0">
                        Generate Query using AI <i className="gradient-font6 bi bi-stars"></i>
                    </label>

                    <div className="d-flex justify-content-end align-items-center gap-2">
                        <input
                            type="number"
                            min={1}
                            className="formField5 ev-btn-small w-15 ev-bold-med ev-radius-xsmall"
                            placeholder="Limit"
                            value={limit ?? ""}
                            onChange={(e) => setLimit(e.target.value ? Number(e.target.value) : null)}
                        />
                        <div className="ev-card-2 d-inline-flex align-items-center gap-2 p-1 ev-radius-xsmall">
                            <span className="ev-xsmall-p-dark text-nowrap">Sort by Date</span>
                            <input
                                type="date"
                                className="formField2 ev-btn-small ev-radius-xlarge ev-padding-xsmall2"
                                value={fromDate}
                                onChange={(e) => {
                                    setFromDate(e.target.value);
                                    fetchDiscoveryDetails(extractYear(e.target.value), extractYear(toDate));
                                }}
                            />
                            <span className="ev-xsmall-p-dark">To</span>
                            <input
                                type="date"
                                className="formField2 ev-btn-small ev-radius-xlarge ev-padding-xsmall2"
                                value={toDate}
                                onChange={(e) => {
                                    setToDate(e.target.value);
                                    fetchDiscoveryDetails(extractYear(fromDate), extractYear(e.target.value));
                                }}
                            />
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-6 d-flex flex-column">
                        <div className="ev-card-6 p-1 ev-radius-xsmall">
                            <div className="form-group">
                                <input
                                    type="text"
                                    className="formField3 bg-transparent"
                                    placeholder="Describe your clinical trial search in plain language..."
                                    value={queryPrompt}
                                    onChange={(e) => setQueryPrompt(e.target.value)}
                                    onKeyPress={(e) => { if (e.key === "Enter") handleGenerateQuery(); }}
                                />
                            </div>
                            <div className="text-end">
                                <button
                                    type="button"
                                    className="ev-btn-gradient-fill-round ev-radius-xsmall ev-bold-reg ev-btn-small"
                                    onClick={handleGenerateQuery}
                                    disabled={!queryPrompt.trim() || isProcessing || isGenerating}
                                >
                                    {isGenerating
                                        ? <><i className="bi bi-hourglass-split me-1"></i> Generating...</>
                                        : <><i className="bi bi-play-fill"></i> Generate</>
                                    }
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="col-6 d-flex flex-column">
                        <div className="form-group">
                            <textarea
                                className="formField3 vh-9 ev-message-card2 w-100 pt-2"
                                value={finalQuery}
                                onChange={(e) => setFinalQuery(e.target.value)}
                                placeholder="e.g. Condition: lung cancer, Intervention: pembrolizumab, Location: USA"
                                style={{ resize: "none" }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Results Panel ── */}
            <div className="ev-card-4 mx-1 px-3 py-2">
                <div className="d-flex justify-content-between align-items-center gap-1 mb-2">
                    <div className="ev-message-card6 ev-radius-xsmall p-1 d-flex gap-2">
                        {[
                            { key: "results",       label: "Results" },
                            { key: "status",        label: "Status Distribution" },
                            { key: "geography",     label: "Geography" },
                            { key: "interventions", label: "Interventions" },
                            { key: "timeline",      label: "Timeline" },
                        ].map((tab) => (
                            <button
                                key={tab.key}
                                className={
                                    activeTab === tab.key
                                        ? "ev-btn-blue-fill-round ev-btn-small ev-radius-xsmall ev-bold-med"
                                        : "ev-btn-light-fill ev-btn-small ev-radius-xsmall ev-bold-med"
                                }
                                onClick={() => setActiveTab(tab.key)}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    <div className="d-flex align-items-center flex-shrink-0 gap-2">
                        <DownloadDropdown
                            downloadType={downloadType}
                            onTypeChange={setDownloadType}
                            onDownload={handleDownload}
                            disabled={activeSelectedIds.length === 0 || isDownloading}
                            isLoading={isDownloading}
                            ctMode={true}
                        />
                        <MigrateDropdown
                            projects={slrProjects}
                            selectedProject={selectedProject}
                            onSelectProject={setSelectedProject}
                            selectedPhase={selectedPhase}
                            onSelectPhase={setSelectedPhase}
                            onMigrate={handleMigrate}
                            disabled={activeSelectedIds.length === 0 || isMigrating}
                            isLoading={isMigrating}
                            abstractMigrationStatus={abstractMigrationStatus}
                            fulltextMigrationStatus={fulltextMigrationStatus}
                            phases={CT_PHASES}
                            dropdownAlign="end"
                        />
                        <StatusBadgeXSm status={searchProcessingStatus} />
                    </div>
                </div>

                {/* ── Content ── */}
                <div className="row">
                    {isSearchRunning && (
                        <div className="col-12">
                            <DiscoveryProgressBar progress={progressBar} />
                        </div>
                    )}

                    {!isSearchRunning && results.length === 0 && (
                        <div className="col-12">
                            <div className="d-flex flex-column align-items-center justify-content-center vh-55 py-5">
                                <i className="bi bi-journal-x ev-fs-50 text-muted mb-3"></i>
                                <p className="ev-reg-p-dark ev-bold-med mb-1">No Clinical Trials Results Found</p>
                                <p className="ev-small-p-dark text-muted text-center mb-0">
                                    Try adjusting your search query or run a new search to find clinical trials.
                                </p>
                            </div>
                        </div>
                    )}

                    {!isSearchRunning && results.length > 0 && activeTab === "results" && (
                        <>
                            <div className="col-5 pe-1">
                                <ClinicalTrialsResultsList
                                    results={results}
                                    selectedResult={selectedResult}
                                    onSelectResult={handleSelectResult}
                                    selectedIds={[...selectedIds]}
                                    selectAll={selectAll}
                                    onSelectAll={(checked) => {
                                        if (checked) setSelectedIds(new Set(results.map(r => r.id)));
                                        else setSelectedIds(new Set());
                                    }}
                                    onSelectOne={(id, checked) => {
                                        const newSet = new Set(selectedIds);
                                        checked ? newSet.add(id) : newSet.delete(id);
                                        setSelectedIds(newSet);
                                    }}
                                />
                            </div>
                            <div className="col-7 ps-1">
                                <ClinicalTrialsDetailPanel trial={selectedResult} />
                            </div>
                        </>
                    )}

                    {/* Status Distribution tab */}
                    {activeTab === "status" && (
                        <div className="col-12">
                            {graphsLoading ? (
                                <div className="ev-card-2 p-4 text-center">
                                    <p className="ev-small-p-dark">Loading status chart...</p>
                                </div>
                            ) : statusGraphData ? (
                                <LiteratureDiscoveryGraphComponent
                                    graphData={statusGraphData}
                                    isLoading={false}
                                    contentId="ct-status"
                                />
                            ) : (
                                <div className="ev-card-2 p-4 text-center">
                                    <p className="ev-small-p-dark">Status distribution not available. Run search first.</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Geography tab */}
                    {activeTab === "geography" && (
                        <div className="col-12">
                            {graphsLoading ? (
                                <div className="ev-card-2 p-4 text-center">
                                    <p className="ev-small-p-dark">Loading geography chart...</p>
                                </div>
                            ) : geoGraphData ? (
                                <LiteratureDiscoveryGraphComponent
                                    graphData={geoGraphData}
                                    isLoading={false}
                                    contentId="ct-geo"
                                />
                            ) : (
                                <div className="ev-card-2 p-4 text-center">
                                    <p className="ev-small-p-dark">Geographical distribution not available. Run search first.</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Interventions tab */}
                    {activeTab === "interventions" && (
                        <div className="col-12">
                            {graphsLoading ? (
                                <div className="ev-card-2 p-4 text-center">
                                    <p className="ev-small-p-dark">Loading interventions chart...</p>
                                </div>
                            ) : interventionsGraphData ? (
                                <LiteratureDiscoveryGraphComponent
                                    graphData={interventionsGraphData}
                                    isLoading={false}
                                    contentId="ct-interventions"
                                />
                            ) : (
                                <div className="ev-card-2 p-4 text-center">
                                    <p className="ev-small-p-dark">Interventions chart not available. Run search first.</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Timeline tab */}
                    {activeTab === "timeline" && (
                        <div className="col-12">
                            {graphsLoading ? (
                                <div className="ev-card-2 p-4 text-center">
                                    <p className="ev-small-p-dark">Loading timeline chart...</p>
                                </div>
                            ) : timelineGraphData ? (
                                <LiteratureDiscoveryGraphComponent
                                    graphData={timelineGraphData}
                                    isLoading={false}
                                    contentId="ct-timeline"
                                />
                            ) : (
                                <div className="ev-card-2 p-4 text-center">
                                    <p className="ev-small-p-dark">Timeline chart not available. Run search first.</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {(isProcessing || isRefreshing || isDownloading || isMigrating || isGenerating) && (
                <ProcessingPopup message={processingMessage || "Processing..."} />
            )}
            {errorMessage && <ErrorPopup onClose={() => setErrorMessage("")} message={errorMessage} />}
            {successMessage && <SuccessPopup onClose={() => setSuccessMessage("")} message={successMessage} />}
        </div>
    );
};

export default ClinicalTrialsDiscoveryDashboard;